import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Space, Tooltip, Input, Skeleton } from "antd";
import {
  BankOutlined,
  ReloadOutlined,
  PlusOutlined,
  SearchOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import { useMessageApi } from "../../../../shared/providers/MessageProvider";
import { ResultsLoadMorePanel } from "../../../../shared";
import CompaniesTableSkeleton from "../Skeleton/CompaniesTableSkeleton";
import "./CompaniesTable.scss";

/**
 * CompaniesTable Component
 * Displays the table of companies with header, search, and actions
 * Search is handled by backend - no client-side filtering
 * Sort is handled by backend - click Company Name header to toggle sort order
 */
const CompaniesTable = ({
  companies = [],
  loading = false,
  isLoadingMore = false,
  hasMore = false,
  totalCompanies = 0,
  canCreate = false,
  searchTerm = "",
  onSearchChange,
  onRefresh,
  onLoadMore,
  onCreateCompany,
  sortOrder = 'asc',
  onSortChange,
}) => {
  const message = useMessageApi();
  const navigate = useNavigate();

  const hasActiveSearch = searchTerm.trim().length > 0;

  // Companies are already filtered by backend search, no client-side filtering needed
  const filteredCompanies = companies;

  const handleRefreshClick = React.useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  const handleSearchChange = React.useCallback((e) => {
    const value = e.target.value;
    onSearchChange?.(value);
  }, [onSearchChange]);

  const handleClearSearch = React.useCallback(() => {
    onSearchChange?.("");
  }, [onSearchChange]);

  const handleCompanyNameSort = React.useCallback(() => {
    onSortChange?.();
  }, [onSortChange]);

  const handleUsersClick = React.useCallback((companyName, userCount) => {
    // Only navigate if there are users in the company
    if (userCount > 0) {
      // Navigate to users page with company filter
      navigate(`/dashboard/users?company=${encodeURIComponent(companyName)}`);
    }
  }, [navigate]);

  const renderEmptyState = React.useCallback(() => {
    if (loading || filteredCompanies.length > 0) return null;

    if (searchTerm) {
      return (
        <div className="empty-state">
          <SearchOutlined className="empty-icon" />
          <p>{`No companies match "${searchTerm}"`}</p>
          <Button type="link" onClick={handleClearSearch}>
            Clear Search
          </Button>
        </div>
      );
    }

    return (
      <div className="empty-state">
        <BankOutlined className="empty-icon" />
        <p>No companies found</p>
        {canCreate && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onCreateCompany}
            className="add-company-btn"
          >
            Add Company
          </Button>
        )}
      </div>
    );
  }, [loading, filteredCompanies.length, searchTerm, handleClearSearch, canCreate, onCreateCompany]);

  return (
    <Card className="user-table-card company-table-card">
      {/* Header */}
      <div className="table-header">
        <div className="table-title">
          <BankOutlined className="table-icon" />
          <span className="table-title-text">
            {loading ? (
              <Skeleton.Input
                active
                size="small"
                style={{ width: 140, height: 18 }}
              />
            ) : (
              <>Companies ({totalCompanies})</>
            )}
          </span>
          <Tooltip
            placement="topLeft"
            title={
              canCreate
                ? "Add New Company"
                : "You don't have permission to create companies"
            }
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onCreateCompany}
              disabled={!canCreate}
              className="table-action-btn add-company-btn title-add-btn"
            />
          </Tooltip>
        </div>
        <div className="table-header-actions">
          <Input
            placeholder="Search companies..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={handleSearchChange}
            allowClear
            className="table-search-input"
          />
          <Space>
            <Tooltip title="Refresh Companies">
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefreshClick}
                loading={loading}
                className="table-action-btn"
              />
            </Tooltip>
          </Space>
        </div>
      </div>

      {/* Table */}
      <div className="user-table-container company-table-container">
        <table className="custom-user-table company-table">
          <thead className="sticky-header">
            <tr>
              <th 
                className="sortable-header company-name-header"
                onClick={handleCompanyNameSort}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>Company Name</span>
                  {sortOrder === 'asc' ? (
                    <ArrowUpOutlined style={{ fontSize: '11px', color: '#52c41a' }} />
                  ) : (
                    <ArrowDownOutlined style={{ fontSize: '11px', color: '#52c41a' }} />
                  )}
                </div>
              </th>
              <th style={{ textAlign: 'right' }}>Users</th>
            </tr>
          </thead>
          <tbody>
            {loading && companies.length === 0 ? (
              <CompaniesTableSkeleton
                rows={50}
              />
            ) : (
              filteredCompanies.map((company) => {
                const userCount = company.user_count || 0;
                const hasUsers = userCount > 0;
                return (
                  <tr key={`${company.id}-${company.name}`} className="company-row">
                    <td className="company-name">
                      <Tooltip title={company.name} placement="top">
                        <span>{company.name}</span>
                      </Tooltip>
                    </td>
                    <td 
                      className={`user-count ${hasUsers ? 'clickable' : ''}`}
                      onClick={() => handleUsersClick(company.name, userCount)}
                      style={{ cursor: hasUsers ? 'pointer' : 'default' }}
                    >
                      <Tooltip 
                        title={hasUsers ? `View ${userCount} user${userCount === 1 ? '' : 's'} from ${company.name}` : 'No users in this company'}
                        placement="left"
                      >
                        <span className="user-count-badge">
                          {userCount}
                        </span>
                      </Tooltip>
                    </td>
                  </tr>
                );
              })
            )}

            {isLoadingMore && filteredCompanies.length > 0 && (
              <CompaniesTableSkeleton
                rows={8}
              />
            )}
          </tbody>
        </table>

        {renderEmptyState()}
      </div>

      {/* Load More Panel */}
      <ResultsLoadMorePanel
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        onLoadMore={(shouldScroll) => {
          if (onLoadMore) {
            onLoadMore(shouldScroll);
            // Auto scroll down to show loading area if shouldScroll is true
            if (shouldScroll) {
              setTimeout(() => {
                window.scrollTo({
                  top: document.documentElement.scrollHeight,
                  behavior: 'smooth'
                });
              }, 100);
            }
          }
        }}
        loading={loading}
        itemsCount={filteredCompanies.length}
        shouldScroll={true}
        forceDisableButton={hasActiveSearch}
      />
    </Card>
  );
};

export default React.memo(CompaniesTable);

